import { useMedplum } from '@medplum/react';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  type CreatePracticePayload,
  type CreateUserPayload,
  createPracticeLive,
  createUserLive,
  fetchAdminDirectory,
} from '../lib/adminDirectoryService';
import type { Practice, User } from '../pages/users/types/rbac';

export interface MedplumDirectoryUsersValue {
  users: User[];
  setUsers: React.Dispatch<React.SetStateAction<User[]>>;
  practices: Practice[];
  setPractices: React.Dispatch<React.SetStateAction<Practice[]>>;
  /** Client Patient ids per Organization (ServiceRequest.performer). */
  clientIdsByOrganizationId: Record<string, string[]>;
  platformOrganization: { id: string; name: string } | null;
  /** List a client under a practice before directory refetch (e.g. right after admin intake). */
  registerClientForOrganization: (patientId: string, organizationId: string) => void;
  loading: boolean;
  error: Error | null;
  reload: () => void;
  createPractice: (payload: CreatePracticePayload) => Promise<Practice>;
  createUser: (payload: CreateUserPayload) => Promise<User>;
  isMock: boolean;
}

const MedplumDirectoryUsersContext = createContext<MedplumDirectoryUsersValue | null>(null);

/**
 * Wraps Settings routes so Practices & Users, practice detail, and `/settings/users/:id`
 * share one directory fetch and in-memory updates (e.g. after create user).
 */
export function MedplumDirectoryUsersProvider({ children }: { children: ReactNode }): ReactElement {
  const medplum = useMedplum();
  const [users, setUsers] = useState<User[]>([]);
  const [practices, setPractices] = useState<Practice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [clientIdsByOrganizationId, setClientIdsByOrganizationId] = useState<
    Record<string, string[]>
  >({});
  const [platformOrganization, setPlatformOrganization] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const reload = useCallback(() => {
    setReloadToken((t) => t + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        medplum.invalidateSearches('Organization');
        medplum.invalidateSearches('Practitioner');
        medplum.invalidateSearches('Patient');
        medplum.invalidateSearches('ServiceRequest');
        const data = await fetchAdminDirectory(medplum);
        if (cancelled) return;
        setUsers(data.users);
        setPractices(data.practices);
        setClientIdsByOrganizationId(data.clientIdsByOrganizationId);
        setPlatformOrganization(data.platformOrganization);
        setError(null);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e : new Error(String(e)));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [medplum, reloadToken]);

  const createPractice = useCallback(
    async (payload: CreatePracticePayload): Promise<Practice> => {
      const practice = await createPracticeLive(medplum, payload);
      // New Organization + owner Practitioner exist in Medplum but are not in local `users` until
      // directory refetch. Invalidate search cache then reload so practice detail user lists match.
      medplum.invalidateSearches('Organization');
      medplum.invalidateSearches('Practitioner');
      medplum.invalidateSearches('Patient');
      medplum.invalidateSearches('Organization');
      medplum.invalidateSearches('Practitioner');
      medplum.invalidateSearches('Patient');
      medplum.invalidateSearches('ServiceRequest');
      reload();
      return practice;
    },
    [medplum, reload],
  );

  const registerClientForOrganization = useCallback(
    (patientId: string, organizationId: string) => {
      const orgId = organizationId.trim();
      const pid = patientId.trim();
      if (!orgId || !pid) return;
      setClientIdsByOrganizationId((prev) => {
        const existing = prev[orgId] ?? [];
        if (existing.includes(pid)) return prev;
        return { ...prev, [orgId]: [...existing, pid] };
      });
    },
    [],
  );

  const createUser = useCallback(
    async (payload: CreateUserPayload): Promise<User> => {
      const user = await createUserLive(medplum, payload);
      setUsers((prev) => {
        if (user.role === 'client' && prev.some((u) => u.id === user.id)) {
          return prev;
        }
        return [...prev, user];
      });
      if (user.practiceId) {
        setPractices((prev) =>
          prev.map((p) =>
            p.id === user.practiceId ? { ...p, userCount: p.userCount + 1 } : p,
          ),
        );
      }
      return user;
    },
    [medplum],
  );

  const value = useMemo(
    () => ({
      users,
      setUsers,
      practices,
      setPractices,
      clientIdsByOrganizationId,
      registerClientForOrganization,
      platformOrganization,
      loading,
      error,
      reload,
      createPractice,
      createUser,
      isMock: false,
    }),
    [
      users,
      practices,
      clientIdsByOrganizationId,
      registerClientForOrganization,
      platformOrganization,
      loading,
      error,
      reload,
      createPractice,
      createUser,
    ],
  );

  return createElement(MedplumDirectoryUsersContext.Provider, { value }, children);
}

/**
 * Users + practices for Settings → Users / Practices & Users.
 * Must be used under {@link MedplumDirectoryUsersProvider}.
 */
export function useMedplumDirectoryUsers(): MedplumDirectoryUsersValue {
  const ctx = useContext(MedplumDirectoryUsersContext);
  if (!ctx) {
    throw new Error('useMedplumDirectoryUsers must be used within MedplumDirectoryUsersProvider');
  }
  return ctx;
}
